import React, { useState } from 'react';
import { Avatar, Cushion, Grid, Hide, Rectangle, Shadow, Typography } from '@pitchero/react-ui';
import PropTypes from 'prop-types';
import { get, map, orderBy, range } from 'lodash';
import Icon from '../../../icon';
import ClubLink from '../../../club-link';
import HeaderCell from '../../../table/header-cell';
import RowCell from '../../../table/row-cell';
import PlayerStatisticsTableKey from './key';

const PlayerStatisticsTable = ({ name, keys, table, teamId, seasonId }) => {
  const { fields, rows } = table;
  const headers = map(fields, (field, key) => ({
    key,
    ...field,
  }));

  const [sortField, setSortField] = useState();
  const [sortDirection, setSortDirection] = useState('desc');

  const sortedRows = sortField
    ? orderBy(rows, (row) => get(row, `stats[${sortField}]`, 0), [sortDirection])
    : rows;

  const handleHeaderClick = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortDirection('desc');
      setSortField(field);
    }
  };

  const renderNamesAndAvatars = () =>
    map(sortedRows, (row, key) => {
      const { player } = row;
      return (
        <React.Fragment key={key}>
          <RowCell style={{ gridColumnStart: 1 }}>
            <Hide maxWidth="tab" component="div">
              <ClubLink
                route="team.squad.profile"
                params={{
                  teamId,
                  profileType: player.profile_type,
                  _profile: player.name,
                  profileId: player.profile_id,
                  seasonId,
                }}
              >
                <Avatar
                  image={player.img_url}
                  includeRetina
                  alt={player.name}
                  size={48}
                  style={{ verticalAlign: 'middle' }}
                />
              </ClubLink>
            </Hide>
          </RowCell>
          <RowCell>
            <Cushion vertical="small">
              <Typography preset="subtitle--small" component="div" align="left">
                <ClubLink
                  route="team.squad.profile"
                  params={{
                    teamId,
                    profileType: player.profile_type,
                    _profile: player.name,
                    profileId: player.profile_id,
                    seasonId,
                  }}
                >
                  <a>{player.name}</a>
                </ClubLink>
              </Typography>
              <Typography preset="description" color="dustygrey" component="div" align="left">
                {player.position}
              </Typography>
            </Cushion>
          </RowCell>
        </React.Fragment>
      );
    });

  const renderStats = () => (
    <div
      style={{
        gridRow: `1 / span ${rows.length + 1}`,
        gridColumnStart: 4,
        overflowX: 'scroll',
        overflowY: 'hidden',
      }}
    >
      <Grid columns={`repeat(${headers.length}, 72px)`} rows="48px">
        {map(headers, (field, index) => {
          const isLast = index === headers.length - 1;
          const { key, short } = field;
          const isSortCol = key === sortField;

          return (
            <Rectangle fill="alabaster" radius={isLast ? 'roundedTopRight' : 0} key={key}>
              <HeaderCell key={key}>
                <Typography
                  onClick={() => handleHeaderClick(key)}
                  color={isSortCol ? 'black' : 'dustygrey'}
                  preset={isSortCol ? 'name' : 'description'}
                  style={{ cursor: 'pointer' }}
                >
                  <Cushion horizontal="xsmall" component="span">
                    {short}
                  </Cushion>
                  {isSortCol &&
                    (sortDirection === 'asc' ? (
                      <Icon name="arrow-up" preset="name" />
                    ) : (
                      <Icon name="arrow-down" preset="name" />
                    ))}
                </Typography>
              </HeaderCell>
            </Rectangle>
          );
        })}

        {map(sortedRows, (row) => (
          <React.Fragment key={row.player.profile_id}>
            {map(row.stats, (stat, key) => (
              <RowCell key={key} typographyColor={key === sortField ? 'black' : 'dustygrey'}>
                {stat}
              </RowCell>
            ))}
          </React.Fragment>
        ))}
      </Grid>
    </div>
  );

  return (
    <>
      <Shadow>
        <Grid
          className="no-grid-hide"
          columns={`96px 200px auto minmax(0, ${headers.length * 72}px)`}
          rows="48px"
          responsive={[{ maxWidth: 'tab', props: { columns: '10px 200px 0 minmax(0, 1fr)' } }]}
        >
          <Rectangle fill="alabaster">
            <HeaderCell colSpan={3} align="left">
              <Cushion
                component="div"
                left="xsmall"
                responsive={[{ minWidth: 'tab', props: { left: 'medium' } }]}
              >
                <Typography color="black" preset="tag--large">
                  {name}
                </Typography>
              </Cushion>
            </HeaderCell>
          </Rectangle>

          {renderNamesAndAvatars()}

          {range(2, rows.length + 2).map((rowNo) => (
            <Rectangle
              key={rowNo}
              fill="white"
              borderBottom="thinLightGrey"
              style={{ gridColumnStart: 3, gridRowStart: rowNo }}
            />
          ))}
          {renderStats()}
        </Grid>
      </Shadow>
      <PlayerStatisticsTableKey keys={keys} />
    </>
  );
};

PlayerStatisticsTable.defaultProps = {
  seasonId: null,
  table: null,
  keys: null,
};

PlayerStatisticsTable.propTypes = {
  seasonId: PropTypes.number,
  table: PropTypes.shape({
    fields: PropTypes.shape({}),
    rows: PropTypes.arrayOf(
      PropTypes.shape({
        player: PropTypes.shape({
          profile_types: PropTypes.string,
          profile_id: PropTypes.string,
          name: PropTypes.string,
          position: PropTypes.string,
          img_url: PropTypes.string,
        }),
        stats: PropTypes.shape({}),
      }),
    ),
  }),
  name: PropTypes.string.isRequired,
  teamId: PropTypes.number.isRequired,
  keys: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      value: PropTypes.string,
    }),
  ),
};

export default PlayerStatisticsTable;
